const toKey = (label) => label.replace(/\?/g, "").trim().toLowerCase().replace(/\s+/g, "_");

const TYPE_MAP = {
  datefilter: "DatePicker",
  daterangefilter: "DateRange",
  booleanfilter: "SearchOptionGroup",
  selectfilter: "SingleSelect",
  multiselectfilter: "MultiSelectSearch",
  dynamicoptions: "MultiSelectSearch",
  decimalrangefilter: "RangeSlider",
};

const DEFAULT_CHIP = { isActive: false, color: "#9ca3af", label: "" };

const stripLookupSuffix = (param = "") => String(param).replace(/(__gte|__lte|__in|__exact)$/i, "");

const normalizeOptions = (options = []) => {
  if (!Array.isArray(options)) {
    return [];
  }
  return options.map((opt) => ({
    label: opt.label ?? opt.name ?? String(opt.key ?? opt.value ?? ""),
    key: opt.key ?? opt.value ?? opt.id ?? opt.pk ?? opt.label ?? "",
  }));
};

const normalizeApiUrl = (apiUrl) => {
  if (typeof apiUrl !== "string") {
    return "";
  }
  return apiUrl.replace(/^\/?api\//, "").replace(/^\/+/, "");
};

const getFieldName = (filter) => {
  const qp = filter?.query_params;

  if (qp && typeof qp === "object") {
    if (qp.multi_select) {
      return stripLookupSuffix(qp.multi_select);
    }
    if (qp.start) {
      return stripLookupSuffix(qp.start);
    }
    if (qp.stop) {
      return stripLookupSuffix(qp.stop);
    }
    if (qp.exact) {
      return stripLookupSuffix(qp.exact);
    }
  }

  if (typeof qp === "string" && qp.trim().length > 0) {
    return qp.trim();
  }
  if (Array.isArray(qp) && qp.length > 0) {
    return String(qp[0]);
  }
  if (qp && typeof qp === "object") {
    const maybeField = qp.field || qp.name || qp.param || qp.key;
    if (maybeField) {
      return String(maybeField);
    }
    const firstKey = Object.keys(qp)[0];
    if (firstKey) {
      return String(firstKey);
    }
  }
  return toKey(filter?.label ?? "");
};

const getBooleanLabels = (options = []) => {
  if (!Array.isArray(options)) {
    return {};
  }
  const trueOpt = options.find((opt) => String(opt.value ?? opt.key).toLowerCase() === "true");
  const falseOpt = options.find((opt) => String(opt.value ?? opt.key).toLowerCase() === "false");
  return {
    customTrueLabel: trueOpt?.label,
    customFalseLabel: falseOpt?.label,
  };
};

export function transformFilters(apiFilters = []) {
  return apiFilters.reduce((acc, filter, index) => {
    const fieldName = getFieldName(filter);
    const filterType = TYPE_MAP[filter.type] ?? null;

    if (!filterType) {
      return acc;
    }

    const inlineOptions = normalizeOptions(filter.options);
    const hasInlineOptions = inlineOptions.length > 0;

    const baseConfig = {
      fieldName,
      title: (filter.label || "").trim(),
      filterType,
      permission: "",
      activeChip: { ...DEFAULT_CHIP },
      order: index,
    };

    switch (filterType) {
      case "DatePicker":
      case "DateRange": {
        acc.push({
          ...baseConfig,
        });
        break;
      }

      case "SearchOptionGroup": {
        const { customTrueLabel, customFalseLabel } = getBooleanLabels(filter.options);
        acc.push({
          ...baseConfig,
          customTrueLabel: customTrueLabel ?? null,
          customFalseLabel: customFalseLabel ?? null,
        });
        break;
      }

      case "SingleSelect": {
        const apiUrl = hasInlineOptions ? "" : normalizeApiUrl(filter.api_url);
        acc.push({
          ...baseConfig,
          apiUrl,
          nameKey: "label",
          valueKey: "key",
          orderingVal: "order",
          ...(hasInlineOptions ? { staticData: inlineOptions, fetchFromAPI: false } : {}),
        });
        break;
      }

      case "MultiSelectSearch": {
        const apiUrl = hasInlineOptions ? "" : normalizeApiUrl(filter.api_url);
        acc.push({
          ...baseConfig,
          searchTitle: (filter.label || "").replace(/filter$/i, "").trim(),
          apiUrl,
          nameKey: "label",
          valueKey: "key",
          orderingVal: "order",
          ...(hasInlineOptions ? { options: inlineOptions, fetchFromAPI: false } : {}),
        });
        break;
      }

      case "RangeSlider": {
        acc.push({
          ...baseConfig,
          min: filter.min ?? null,
          max: filter.max ?? null,
        });
        break;
      }

      default:
        acc.push(baseConfig);
    }

    return acc;
  }, []);
}
